import { useState } from "react";
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Link, Stack, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, Typography,
} from "@mui/material";
import { GoogleLogin, type CredentialResponse } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import {
  fetchMyApprovals, signInForMyApprovals, signInRequired, type ApprovalEvent, type BillApproval,
} from "@/api/approvals";
import { errorMessage } from "@/api/closeReview";
import { GOOGLE_HOSTED_DOMAIN } from "@/config";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (n: number | null) => (n == null ? "" : USD.format(n));
const when = (d: string | null) => (d ? dayjs(d).format("MMM D, YYYY") : "");
const KEY = ["my-approvals"];

// An approver's own queue and history. Same Google sign-in as the single-bill
// page, scoped to the signed-in account; it opens nothing else in the app.
export function MyApprovalsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: fetchMyApprovals, retry: false });
  const [signInError, setSignInError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const signIn = useMutation({
    mutationFn: (credential: string) => signInForMyApprovals(credential),
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
    onError: (e) => setSignInError(errorMessage(e)),
  });
  const needsSignIn = signInRequired(query.error);
  const data = query.data;
  const rejections = (data?.history ?? []).filter((e) => e.action === "rejected");

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box component="img" src="/logo.png" alt="Freedom Power" sx={{ height: 32 }} />
        <Typography variant="h6">My bill approvals</Typography>
      </Stack>

      {query.isLoading && <CircularProgress />}

      {needsSignIn && (
        <Card sx={{ maxWidth: 440 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography>Sign in with your Freedom Power Google account to see the bills waiting for you.</Typography>
              {signInError && <Alert severity="error">{signInError}</Alert>}
              <Box>
                <GoogleLogin
                  hosted_domain={GOOGLE_HOSTED_DOMAIN || undefined}
                  onSuccess={(resp: CredentialResponse) => { setSignInError(null); signIn.mutate(resp.credential ?? ""); }}
                  onError={() => setSignInError("Google sign-in failed.")}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {query.isError && !needsSignIn && <Alert severity="error">{errorMessage(query.error)}</Alert>}

      {data && (
        <Stack spacing={2}>
          <Typography color="text.secondary">Signed in as {data.name ?? data.email}</Typography>
          {!data.live && <Alert severity="info">Approvals aren't live for you here yet. Keep approving in Charted for now.</Alert>}
          <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
            <Tab label={`Waiting (${data.waiting.length})`} />
            <Tab label={`Sent back to AP (${data.sent_back.length})`} />
            <Tab label="History" />
            <Tab label={`Rejections (${rejections.length})`} />
          </Tabs>
          {tab === 0 && <Waiting bills={data.waiting} empty="Nothing waiting for you." />}
          {tab === 1 && <Waiting bills={data.sent_back} empty="Nothing sent back." sentBack />}
          {tab === 2 && <History events={data.history} empty="No decisions yet." />}
          {tab === 3 && <History events={rejections} empty="You haven't rejected any bills." showResubmitted />}
        </Stack>
      )}
    </Box>
  );
}

function Waiting({ bills, empty, sentBack = false }: { bills: BillApproval[]; empty: string; sentBack?: boolean }) {
  if (bills.length === 0) return <Typography color="text.secondary">{empty}</Typography>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell><TableCell>Invoice</TableCell><TableCell align="right">Amount</TableCell>
            <TableCell>{sentBack ? "Should go to" : "Waiting since"}</TableCell><TableCell>Entered by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bills.map((b) => (
            <TableRow key={b.token} hover>
              <TableCell><Link component={RouterLink} to={`/approvals/${b.token}`}>{b.vendor_name}</Link></TableCell>
              <TableCell>{b.tranid}</TableCell>
              <TableCell align="right">{usd(b.amount)}</TableCell>
              <TableCell>{sentBack ? b.reroute?.to_name : when(b.pending_since)}</TableCell>
              <TableCell>{b.submitter_name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const ACTION_LABEL: Record<ApprovalEvent["action"], string> = { approved: "Approved", rejected: "Rejected", sent_back: "Sent back" };
const ACTION_COLOR: Record<ApprovalEvent["action"], "success" | "error" | "default"> = {
  approved: "success", rejected: "error", sent_back: "default",
};

function History({ events, empty, showResubmitted = false }: {
  events: ApprovalEvent[]; empty: string; showResubmitted?: boolean;
}) {
  if (events.length === 0) return <Typography color="text.secondary">{empty}</Typography>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell><TableCell>What</TableCell><TableCell>Vendor</TableCell><TableCell>Invoice</TableCell>
            <TableCell align="right">Amount</TableCell><TableCell>Comment</TableCell>
            {showResubmitted && <TableCell>Now</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((e, i) => (
            <TableRow key={`${e.token}-${e.action}-${i}`} hover>
              <TableCell>{when(e.at)}</TableCell>
              <TableCell><Chip size="small" label={ACTION_LABEL[e.action]} color={ACTION_COLOR[e.action]} /></TableCell>
              <TableCell><Link component={RouterLink} to={`/approvals/${e.token}`}>{e.vendor_name}</Link></TableCell>
              <TableCell>{e.tranid}</TableCell>
              <TableCell align="right">{usd(e.amount)}</TableCell>
              <TableCell>{e.action === "sent_back" && e.to_name ? `To ${e.to_name}: ` : ""}{e.comment}</TableCell>
              {showResubmitted && <TableCell>{e.status === "rejected" ? "Not resubmitted" : e.status === "pending" ? "Resubmitted, waiting" : e.status}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
